/**
 * The angle an article is written from.
 *
 * The first decision a human editor makes, and the one this product never asked
 * for: every article was built from the same brief with a different keyword, so
 * every article came out the same shape. An angle changes which headings get
 * proposed - not how they are phrased - which is the difference between two
 * articles and one article written twice.
 *
 * Shared because three places must agree: the screens that offer it, the
 * proposals prompt that plans to it, and the body prompt that writes to it. The
 * key goes in the column (migration 049); the label and the brief are wording
 * and live here, where changing them rewrites no rows.
 */
export const ARTICLE_ANGLES = {
  compare: {
    label: '比較・使い分け',
    hint: '似た選択肢との違いを整理する',
    /** What the outline should be built around. Sent to the model verbatim. */
    brief:
      '似た選択肢との違いを整理する記事です。比較軸（効果の出方・ダウンタイム・費用・回数・向いている人）を決め、表で並べて示します。どちらが優れているかではなく、どの読者にどちらが向くかを書きます。',
  },
  howto: {
    label: '手順・流れ',
    hint: '当日までの流れと準備を追う',
    brief:
      '受ける前から受けた後までの流れを順に追う記事です。カウンセリング・当日・直後・その後の生活を時系列で並べ、各段階で読者がすることと注意点を具体的に書きます。',
  },
  avoid: {
    label: '失敗回避・不安の解消',
    hint: '後悔しやすい点と避け方',
    brief:
      '後悔につながりやすい点を先に挙げ、その避け方を示す記事です。誇張せず、起こりうることと起こりにくいことを分けて書きます。不安をあおって行動させる書き方はしません。',
  },
  cost: {
    label: '費用・回数',
    hint: '総額と回数の目安をはっきり',
    brief:
      '費用の判断材料をそろえる記事です。1回あたりと想定回数、総額の目安、追加でかかりうるもの、費用に影響する条件を、承認済みの価格の範囲で表に整理します。',
  },
  case: {
    label: '症例・向いている人',
    hint: 'どんな悩みに向くか',
    brief:
      'どのような悩みや状態に向くのかを整理する記事です。効果を保証せず、向いている場合と向かない場合、判断の目安を書きます。個別の症例写真や体験談は使いません。',
  },
} as const;

export type ArticleAngle = keyof typeof ARTICLE_ANGLES;

/** Whether a value from the wire is an angle this build knows. */
export const isAngle = (value: unknown): value is ArticleAngle =>
  typeof value === 'string' && value in ARTICLE_ANGLES;
